import React, { useCallback, useEffect, useMemo } from 'react';
import Slider from '@material-ui/core/Slider';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { styled } from '@this/constants/themes';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type HotelList from '@this/src/domain/hotel/hotel_list';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox/checkbox';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { formatPrice } from '@this/src/util';

const PRICE_STEP = 1000;
const DISTANCE_STEP = 100;

interface Props {
  hotels: HotelList;
  hotelPriceLimit: number | null;
  isMobile: boolean;
}

interface ValueLabelComponentProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

interface StyleProps {
  isMobile: boolean;
}

const ListAreaHotelFilter: React.FC<Props> = ({ hotels, hotelPriceLimit, isMobile }) => {
  const filter = hotels.filter;
  const hasPriceLimit = hotelPriceLimit != null && hotelPriceLimit > 0;
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  useEffect(() => {
    hotels.setHotelPriceLimit(hotelPriceLimit);
  }, [hotels, hotelPriceLimit]);

  const [pMarks, dMarks] = useMemo(
    () => [hotels.priceSliderMarks(PRICE_STEP), hotels.distanceSliderMarks(DISTANCE_STEP)],
    [hotels]
  );
  const minPrice = useMemo(() => hotels.minPriceValue(filter.minPrice, pMarks), [filter.minPrice, pMarks]);
  const maxPrice = useMemo(() => hotels.maxPriceValue(filter.maxPrice, pMarks), [filter.maxPrice, pMarks]);
  const minDistance = useMemo(
    () => hotels.minDistanceValue(filter.minDistance, dMarks),
    [filter.minDistance, dMarks]
  );
  const maxDistance = useMemo(
    () => hotels.maxDistanceValue(filter.maxDistance, dMarks),
    [filter.maxDistance, dMarks]
  );

  const handlePriceChange = useCallback(
    (_: unknown, newValue: number | number[]) => hotels.handlePriceChange(newValue, pMarks),
    [hotels, pMarks]
  );

  const handleDistanceChange = useCallback(
    (_: unknown, newValue: number | number[]) => hotels.handleDistanceChange(newValue, dMarks),
    [hotels, dMarks]
  );

  const priceValueLabelFormat = useCallback(
    (value: number) => hotels.priceValueLabelFormat(value, pMarks),
    [hotels, pMarks]
  );

  const distanceValueLabelFormat = useCallback(
    (value: number) => hotels.distanceValueLabelFormat(value, dMarks),
    [hotels, dMarks]
  );

  return (
    <Wrapper isMobile={isMobile}>
      <Title>ホテルの絞り込み</Title>
      {hasPriceLimit && (
        <div style={styles.limitCheckboxSection}>
          <Checkbox checked={filter.onlyWithinLimit} onChange={() => hotels.toggleOnlyWithinLimit()}>
            <span style={styles.limitCheckboxLabel}>規定以内のみ表示する</span>
          </Checkbox>
          <span style={styles.limitSubText}>規程金額：{formatPrice(hotelPriceLimit as number)}/泊</span>
        </div>
      )}
      <Section>
        <SectionTitle>価格帯（１泊あたり）</SectionTitle>
        <div style={rangeInputRowStyle}>
          <label htmlFor="hotel-price-min" style={rangeInputFieldStyle}>
            <span style={rangeInputLabelStyle}>下限</span>
            <Input
              id="hotel-price-min"
              size="small"
              type="number"
              value={filter.priceInput.min}
              onChange={event => hotels.setPriceInput('min', event.target.value)}
              onBlur={() => hotels.normalizePriceInput()}
            />
          </label>
          <label htmlFor="hotel-price-max" style={rangeInputFieldStyle}>
            <span style={rangeInputLabelStyle}>上限</span>
            <Input
              id="hotel-price-max"
              size="small"
              type="number"
              value={filter.priceInput.max}
              onChange={event => hotels.setPriceInput('max', event.target.value)}
              onBlur={() => hotels.normalizePriceInput()}
            />
          </label>
        </div>
        {filter.priceInputError && <div style={rangeInputErrorStyle}>{filter.priceInputError}</div>}
        <div style={styles.priceSliderArea}>
          {hasPriceLimit && filter.onlyWithinLimit && (
            <div style={styles.autoAdjustBadge}>
              <span style={styles.autoAdjustLabel}>自動調整中</span>
              <span style={styles.autoAdjustValue}>規程金額 {formatPrice(hotelPriceLimit as number)}</span>
            </div>
          )}
          <SliderSection>
            {isMobile ? (
              <CustomSliderSp
                value={[minPrice, maxPrice]}
                marks={pMarks}
                min={pMarks[0].value}
                max={pMarks[1].value}
                step={PRICE_STEP}
                ValueLabelComponent={ValueLabelComponent}
                valueLabelFormat={priceValueLabelFormat}
                onChange={handlePriceChange}
              />
            ) : (
              <CustomSlider
                value={[minPrice, maxPrice]}
                marks={pMarks}
                min={pMarks[0].value}
                max={pMarks[1].value}
                step={PRICE_STEP}
                ValueLabelComponent={ValueLabelComponent}
                valueLabelFormat={priceValueLabelFormat}
                onChange={handlePriceChange}
              />
            )}
          </SliderSection>
        </div>
      </Section>
      <Divider />
      <Section>
        <SectionTitle>目的地までの距離</SectionTitle>
        <div style={rangeInputRowStyle}>
          <label htmlFor="hotel-distance-min" style={rangeInputFieldStyle}>
            <span style={rangeInputLabelStyle}>下限</span>
            <Input
              id="hotel-distance-min"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.1 }}
              value={filter.distanceInput.min}
              onChange={event => hotels.setDistanceInput('min', event.target.value)}
              onBlur={() => hotels.normalizeDistanceInput('min')}
            />
          </label>
          <label htmlFor="hotel-distance-max" style={rangeInputFieldStyle}>
            <span style={rangeInputLabelStyle}>上限</span>
            <Input
              id="hotel-distance-max"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.1 }}
              value={filter.distanceInput.max}
              onChange={event => hotels.setDistanceInput('max', event.target.value)}
              onBlur={() => hotels.normalizeDistanceInput('max')}
            />
          </label>
        </div>
        {filter.distanceInputError && <div style={rangeInputErrorStyle}>{filter.distanceInputError}</div>}
        <SliderSection>
          {isMobile ? (
            <CustomSliderSp
              value={[minDistance, maxDistance]}
              marks={dMarks}
              min={dMarks[0].value}
              max={dMarks[1].value}
              step={DISTANCE_STEP}
              ValueLabelComponent={ValueLabelComponent}
              valueLabelFormat={distanceValueLabelFormat}
              onChange={handleDistanceChange}
            />
          ) : (
            <CustomSlider
              value={[minDistance, maxDistance]}
              marks={dMarks}
              min={dMarks[0].value}
              max={dMarks[1].value}
              step={DISTANCE_STEP}
              ValueLabelComponent={ValueLabelComponent}
              valueLabelFormat={distanceValueLabelFormat}
              onChange={handleDistanceChange}
            />
          )}
        </SliderSection>
      </Section>
      <Actions isMobile={isMobile}>
        <Link color="primary" onClick={() => hotels.clearFilter()}>
          リセット
        </Link>
        {isMobile ? (
          <Button color="primary" onClick={() => hotels.toggleShowFilter()}>
            絞り込む
          </Button>
        ) : (
          <Button color="sub" endIcon={<ChevronRightIcon />} onClick={() => hotels.toggleShowFilter()}>
            閉じる
          </Button>
        )}
      </Actions>
    </Wrapper>
  );
};

const ValueLabelComponent = (props: ValueLabelComponentProps) => {
  const { children, open, value } = props;

  return (
    <Tooltip open={open} enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
};

const Wrapper = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: ${props => (props.isMobile ? '10px' : '0')};
`;

const Title = styled.h2`
  font-size: 13px;
  font-weight: bold;
  margin: 1em 0 0.75em;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: bold;
  margin: 10px 0;
`;

const SliderSection = styled.div`
  font-size: 11px;
  padding: 0 20px;
`;

const CustomSlider = styled(Slider)`
  .MuiSlider-markLabel {
    font-size: 11px;
  }
`;

const CustomSliderSp = styled(CustomSlider)`
  .MuiSlider-markLabel {
    font-size: 11px;
  }

  color: #c4c4c4;

  .MuiSlider-thumb {
    background-color: ${props => props.theme.fieldBgColor};
    border: 2px solid ${props => props.theme.accentColor};
    width: 25px;
    height: 25px;
    margin-top: 0px;
    top: 50%;
    transform: translateY(-50%);
  }

  .MuiSlider-rail {
    height: 4px;
  }

  .MuiSlider-track {
    height: 4px;
    background-color: ${props => props.theme.accentColor};
  }
`;

const Actions = styled.div<StyleProps>`
  display: flex;
  justify-content: space-between;
  margin: ${props => (props.isMobile ? '20px 10px' : '0')};
`;

const getStyles = (theme: AITTheme) => ({
  limitCheckboxSection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4,
    padding: 12,
    backgroundColor: theme.grayBgColorLight,
    border: `1px solid ${theme.fieldBorderColor}`,
    borderRadius: 4
  },
  limitCheckboxLabel: {
    fontSize: 13,
    fontWeight: 'bold' as const
  },
  limitSubText: {
    fontSize: 11,
    color: theme.textColor,
    paddingLeft: 28
  },
  priceSliderArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4
  },
  autoAdjustBadge: {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    alignSelf: 'flex-start' as const,
    gap: 6,
    padding: '2px 8px',
    backgroundColor: theme.accentColor,
    color: '#fff',
    borderRadius: 10,
    fontSize: 10
  },
  autoAdjustLabel: {
    fontWeight: 'bold' as const
  },
  autoAdjustValue: {
    fontWeight: 'normal' as const
  }
});

const rangeInputRowStyle = {
  display: 'flex',
  gap: 4
} as const;

const rangeInputFieldStyle = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: 2
} as const;

const rangeInputLabelStyle = {
  fontSize: 11
} as const;

const rangeInputErrorStyle = {
  color: '#d32f2f',
  fontSize: 11
} as const;

export default ListAreaHotelFilter;
